import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Phone, Zap, ShieldCheck, Gauge } from "lucide-react";
import { BRAND, IMAGES, STATS } from "../../mock";
import { openForm } from "./Pricing";

const Counter = ({ value, suffix }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    let started = false;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started) {
        started = true;
        const start = performance.now();
        const dur = 1800;
        const tick = (t) => {
          const p = Math.min((t - start) / dur, 1);
          setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, [value]);
  return <span ref={ref}>{n.toLocaleString()}{suffix}</span>;
};

const Hero = () => (
  <section id="top" className="relative">
    <div className="relative flex min-h-[100vh] items-start overflow-hidden lg:items-center">
      <img src={IMAGES.hero} alt="Black performance car at dusk" className="hero-zoom absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#08090b] via-[#08090b]/85 to-[#08090b]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#08090b] via-transparent to-transparent" />
      <div className="container-x relative z-10 pb-20 pt-28 sm:pt-32 lg:pb-24 lg:pt-36">
        <div className="max-w-[720px]">
          <div className="fade-up mb-7 inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-4 py-1.5 text-[13px] font-medium text-white/90">
            <span className="pulse-dot h-2 w-2 rounded-full bg-brand" />
            {BRAND.area} Area · Mobile & Workshop Service
          </div>
          <p className="fade-up font-display text-2xl font-semibold uppercase tracking-[0.25em] text-white/70" style={{ animationDelay: ".1s" }}>Professional</p>
          <h1 className="fade-up font-display text-[56px] font-bold uppercase leading-[0.95] text-white sm:text-[84px] lg:text-[104px]" style={{ animationDelay: ".2s" }}>Car Remapping</h1>
          <p className="fade-up mt-4 font-display text-3xl font-semibold text-white sm:text-4xl" style={{ animationDelay: ".3s" }}>
            Unlock your vehicle's <span className="italic text-brand">full potential</span>
          </p>
          <p className="fade-up mt-6 max-w-[580px] text-lg leading-relaxed text-white/65" style={{ animationDelay: ".4s" }}>
            Extra power, quicker response and a smoother drive. Specialist ECU tuning, diagnostics and tailored solutions for cars, SUVs, vans and 4x4s.
          </p>
          <div className="fade-up mt-9 flex flex-wrap gap-4" style={{ animationDelay: ".5s" }}>
            <button onClick={() => openForm("booking")} className="btn-red group flex items-center gap-2 rounded-lg px-7 py-4 text-base font-semibold" data-testid="hero-book-btn">
              Book Now <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => openForm("enquiry")} className="flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-colors hover:border-white/40 hover:bg-white/10" data-testid="hero-quote-btn">
              Get a Free Quote
            </button>
            <a href={BRAND.phoneHref} className="flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-colors hover:border-white/40 hover:bg-white/10" data-testid="hero-call-btn">
              <Phone className="h-5 w-5" /> Call / WhatsApp
            </a>
          </div>
          <div className="fade-up mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/75" style={{ animationDelay: ".6s" }}>
            <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-brand" />More power & torque</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" />Safe & reliable</span>
            <span className="flex items-center gap-2"><Gauge className="h-4 w-4 text-brand" />UK tailored tuning</span>
          </div>
        </div>
      </div>
    </div>
    <div className="border-y border-white/10 bg-[#0d0e12]">
      <div className="container-x grid grid-cols-2 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <div key={s.label} className={`py-9 text-center ${i % 2 === 0 ? "border-r" : ""} ${i === 1 ? "lg:border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} border-white/10`} data-testid={`stat-${i}`}>
            <div className="font-display text-4xl font-bold text-white sm:text-5xl"><Counter value={s.value} suffix={s.suffix} /></div>
            <div className="mt-1 text-[12px] font-medium uppercase tracking-[0.15em] text-white/45">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Hero;
